import { type FieldValues, type UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Select as WFSelect, type SelectProps } from '@wf/components';

import { type LicenseFieldProps } from './LicenseFieldController';

import { useLicenseController } from '../../hooks/useLicenseController';
import { HeaderTabFields } from '../../types/fieldEnums';

const enumSelectFields = { [HeaderTabFields.typeOfUse]: HeaderTabFields.typeOfUse };

export const LicenseSelectField = ({
  name,
  defaultValue,
  configName,
  featureFlag,
  viewMode,
  rules,
  shouldUnregister,
  control,
  isClearable = true,
  disabled,
  ...rest
}: SelectProps<SelectOption> & LicenseFieldProps & UseControllerProps<FieldValues, any>) => {
  const { control: contextControl } = useFormContext();
  const { t } = useTranslation();

  const {
    field: { value, onChange, onBlur, ...fieldRest },
    computedProps,
    config,
  } = useLicenseController({
    name,
    configName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue: defaultValue ?? null,
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });

  const handleChange: typeof rest.onChange = (...args) => {
    onChange(...args);

    if ('onChange' in rest && typeof rest.onChange === 'function') {
      rest.onChange(...args);
    }
  };

  const handleBlur: typeof rest.onBlur = (...args) => {
    onBlur();

    if ('onBlur' in rest && typeof rest.onBlur === 'function') {
      rest.onBlur(...args);
    }
  };

  if (config.isHidden) {
    return <></>;
  }

  const translatedValue = name in enumSelectFields && value ? { ...value, label: t(value.value) } : value;

  return (
    <WFSelect
      {...fieldRest}
      {...computedProps}
      {...rest}
      isClearable={isClearable}
      value={translatedValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};
